import React, { useState, useRef, useEffect } from "react";
import { MapContainer, TileLayer, FeatureGroup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw";
import { loadGoogleMapsApi } from "../../utils/loadGoogleMaps";
import { getToken } from "../../services/tokenGET";
import { saveMockPins } from "../../data/mockPins";

// Checkar om det kör locally (detta är bara för oss)
const IS_LOCAL_DEV = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// Google Maps API tile integration för leaflet
const GoogleMutantLayer = () => {
  const map = useMap();
  const [apiLoaded, setApiLoaded] = useState(false);

  useEffect(() => {
    loadGoogleMapsApi()
      .then(() => setApiLoaded(true))
      .catch(err => console.error('Failed to load Google Maps:', err));
  }, []);

  useEffect(() => {
    if (!map || !apiLoaded) return;

    const googleLayer = L.gridLayer.googleMutant({
      type: "roadmap",
    });
    googleLayer.addTo(map);

    return () => {
      if (googleLayer) {
        map.removeLayer(googleLayer);
      }
    };
  }, [map, apiLoaded]);

  return null;
};

// Komponent för Leaflet Draw kontroll
const DrawControl = ({ featureGroupRef, onPolygonChange, isDrawing, setIsDrawing }) => {
  const map = useMap();
  const drawControlRef = useRef(null);

  useEffect(() => {
    if (!map || !featureGroupRef.current) return;

    const featureGroup = featureGroupRef.current;

    // Controls för ritverktyg
    const drawControl = new L.Control.Draw({
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: true,
        },
        polyline: false,
        rectangle: false,
        circle: false,
        marker: false,
        circlemarker: false,
      },
      edit: {
        featureGroup: featureGroup,
        edit: true,
        remove: false,
      },
    });

    map.addControl(drawControl);
    drawControlRef.current = drawControl;

    // Hantera skapande av polygon
    const handleCreated = (e) => {
      const layer = e.layer;
      featureGroup.clearLayers();
      featureGroup.addLayer(layer);
      onPolygonChange(layer);
      setIsDrawing(false);
    };

    // Hantering för polygon-redigering
    const handleEdited = (e) => {
      const layers = e.layers;
      layers.eachLayer((layer) => {
        onPolygonChange(layer);
      });
    };

    // Hantering för om riting börjat
    const handleDrawStart = () => {
      setIsDrawing(true);
    };

    // Hantering för om ritning slutas
    const handleDrawStop = () => {
      setIsDrawing(false);
    };

    map.on(L.Draw.Event.CREATED, handleCreated);
    map.on(L.Draw.Event.EDITED, handleEdited);
    map.on(L.Draw.Event.DRAWSTART, handleDrawStart);
    map.on(L.Draw.Event.DRAWSTOP, handleDrawStop);

    return () => {
      map.off(L.Draw.Event.CREATED, handleCreated);
      map.off(L.Draw.Event.EDITED, handleEdited);
      map.off(L.Draw.Event.DRAWSTART, handleDrawStart);
      map.off(L.Draw.Event.DRAWSTOP, handleDrawStop);
      if (drawControlRef.current) {
        map.removeControl(drawControlRef.current);
      }
    };
  }, [map, featureGroupRef, onPolygonChange, setIsDrawing]);

  return null;
};

const AddNewAreaModal = ({ isOpen, onClose, onAreaAdded }) => {
  const [areaName, setAreaName] = useState("");
  const [areaDescription, setAreaDescription] = useState("");
  const [areaImage, setAreaImage] = useState(null);
  const [polygonLayer, setPolygonLayer] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const featureGroupRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePolygonChange = (layer) => {
    setPolygonLayer(layer);
  };

  const handleReset = () => {
    if (featureGroupRef.current) {
      featureGroupRef.current.clearLayers();
      setPolygonLayer(null);
    }
    setEditMode(false);
  };

  const handleEditToggle = () => {
    if (polygonLayer && polygonLayer.editing) {
      if (editMode) {
        polygonLayer.editing.disable();
        setEditMode(false);
      } else {
        polygonLayer.editing.enable();
        setEditMode(true);
      }
    }
  };

  const handleSave = async () => {
    //Validering
    if (!areaName.trim()) {
      alert("Vänligen ange ett områdesnamn.");
      return;
    }

    if (!areaDescription.trim()) {
      alert("Vänligen ange en områdesbeskrivning.");
      return;
    }

    if (!areaImage) {
      alert("Vänligen välj en bild för området.");
      return;
    }

    if (!polygonLayer) {
      alert("Vänligen rita ett område på kartan.");
      return;
    }

    setIsSaving(true);

    try {
      // Get polygon coordinates från Leaflet layer
      const latlngs = polygonLayer.getLatLngs()[0].map((latlng, i) => ({
        lat: latlng.lat,
        lng: latlng.lng,
        order: i,
      }));

      // local dev mock area skapas här
      if (IS_LOCAL_DEV) {
        // BARA FÖR LOCAL (skapar mock area och sparar mock pins lokalt)
        const mockArea = {
          id: Date.now(),
          name: areaName.trim(),
          description: areaDescription.trim(),
          image_path: areaImage?.name || "placeholder.jpg"
        };
        
        // Spara mock pins
        saveMockPins(mockArea.id, latlngs);
        
        alert("Nytt område skapat! (lokal mock)");
        if (onAreaAdded) onAreaAdded(mockArea);
        handleClose();
        return;
      }

      const token = await getToken();

      //create form data for the area
      const formData = new FormData();
      formData.append("name", areaName.trim());
      formData.append("description", areaDescription.trim());
      formData.append("image", areaImage);

      // Debug logging
      console.log("Sending area data:", {
        name: areaName.trim(),
        description: areaDescription.trim(),
        image: areaImage?.name,
        imageType: areaImage?.type,
        imageSize: areaImage?.size
      });

      //step 1: Create the area
      const areaResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/areas`, {
        method: "POST", // Use POST for FormData(?)
        headers: {
          "X-CSRF-TOKEN": token,
        },
        credentials: "include",
        body: formData,
      });

      if (!areaResponse.ok) {
        const errorData = await areaResponse.json();
        console.error("Error creating area:", errorData);
        
        // !!!!Display detailed validation errors!!!!!
        if (errorData.name || errorData.description || errorData.image) {
          const errors = [];
          if (errorData.name) errors.push(`Namn: ${errorData.name.join(', ')}`);
          if (errorData.description) errors.push(`Beskrivning: ${errorData.description.join(', ')}`);
          if (errorData.image) errors.push(`Bild: ${errorData.image.join(', ')}`);
          alert(`Valideringsfel:\n${errors.join('\n')}`);
        }
        
        throw new Error("Failed to create area");
      }

      const areaData = await areaResponse.json();
      const newAreaId = areaData.area.id;


      // Debug logging
      console.log("Area created successfully with ID:", newAreaId);
      console.log("Full area response:", areaData);
      console.log("Preparing to send pins:", latlngs);
      // Liten delay för att säkerställa att backenden är redo (kan tas bort vid lösning)
      await new Promise(resolve => setTimeout(resolve, 500));

      
      //step 2: Add pins for the new area
      const pinsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/pins/${newAreaId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-TOKEN": token,
        },
        credentials: "include",
        body: JSON.stringify({ area: newAreaId, pins: latlngs }),
      });

      if (!pinsResponse.ok) {
        // Om pinsen failar, borde vi kanske ta bort det skapade området? Just nu bara ger vi ett felmeddelande.
        const errorText = await pinsResponse.text();
        console.error("Failed to add pins for new area. Status:", pinsResponse.status);
        console.error("Error response:", errorText);
        alert("Området skapades men koordinaterna kunde inte sparas.");
      } else {
        alert("Nytt område skapat framgångsrikt!");
      }

      // Notify parent and close
      if (onAreaAdded) onAreaAdded(areaData.area);
      handleClose();
    } catch (error) {
      console.error("Error creating new area:", error);
      alert("Ett fel uppstod vid skapande av området.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setAreaName("");
    setAreaDescription("");
    setAreaImage(null);
    setPolygonLayer(null);
    setIsDrawing(false);
    setEditMode(false);
    if (featureGroupRef.current) {
      featureGroupRef.current.clearLayers();
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Map Container - Full screen */}
      <div className="fixed inset-0 z-40">
        <MapContainer
          center={[55.59, 13.02]}
          zoom={13}
          style={{ width: "100%", height: "100%", zIndex: 0 }}
        >
          <GoogleMutantLayer />
          <FeatureGroup ref={featureGroupRef}>
            {/* Polygon will be added here */}
          </FeatureGroup>
          <DrawControl 
            featureGroupRef={featureGroupRef} 
            onPolygonChange={handlePolygonChange}
            isDrawing={isDrawing}
            setIsDrawing={setIsDrawing}
          />
        </MapContainer>
      </div>

      {/* Control Panel - Bottom on mobile, top-right floating on desktop */}
      <div 
        className="fixed z-50 bg-white border-forest shadow-2xl overflow-y-auto flex flex-col"
        style={
          isDesktop
            ? {
                top: '1rem',
                right: '1rem',
                left: 'auto',
                bottom: 'auto',
                width: '24rem',
                maxHeight: 'calc(100vh - 2rem)',
                
                borderRadius: '0.5rem'
              }
            : {
                bottom: 0,
                left: 0,
                right: 0,
                
                maxHeight: '70vh',
                margin: 0,
                borderRadius: 0
              }
        }
      >
        <div className="p-6 flex-1">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-2xl font-bold text-charcoal">
              Lägg till nytt område
            </h2>
            <div className="flex items-center gap-2">
              {/* Instructions Button */}
              <button
                onClick={() => setShowInstructions(true)}
                className="w-8 h-8 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg flex items-center justify-center transition-colors"
                title="Visa instruktioner"
                disabled={isSaving}
              >
                ?
              </button>
              <button
                onClick={handleClose}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                disabled={isSaving}
              >
                ✕
              </button>
            </div>
          </div>

            <div className="space-y-4">
              {/* Area Name Input */}
              <div>
                <label className="block text-sm font-semibold text-charcoal mb-2">
                  Områdesnamn *
                </label>
                <input
                  type="text"
                  value={areaName}
                  onChange={(e) => setAreaName(e.target.value)}
                  placeholder="Ange namn för det nya området"
                  className="w-full px-4 py-2 border-2 border-sage rounded-lg focus:outline-none focus:ring-2 focus:ring-forest"
                  disabled={isSaving}
                />
              </div>

              {/* Area Description Input */}
              <div>
                <label className="block text-sm font-semibold text-charcoal mb-2">
                  Områdesbeskrivning *
                </label>
                <textarea
                  value={areaDescription}
                  onChange={(e) => setAreaDescription(e.target.value)}
                  placeholder="Beskriv området"
                  rows="2"
                  className="w-full px-4 py-2 border-2 border-sage rounded-lg focus:outline-none focus:ring-2 focus:ring-forest resize-none"
                  disabled={isSaving}
                />
              </div>

              {/* Area Image Upload */}
              <div>
                <label className="block text-sm font-semibold text-charcoal mb-2">
                  Områdesbild *
                </label>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={(e) => setAreaImage(e.target.files[0])}
                  className="w-full px-4 py-2 border-2 border-sage rounded-lg focus:outline-none focus:ring-2 focus:ring-forest file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-forest file:text-white hover:file:bg-leaf"
                  disabled={isSaving}
                />
                {areaImage && (
                  <p className="mt-2 text-sm text-gray-600">
                    Vald fil: {areaImage.name}
                  </p>
                )}
              </div>

              {/* Control Buttons */}
              <div className="flex flex-wrap gap-2">
                {/* Draw Button - Highlight when active */}
                <button
                  className={`flex-1 min-w-[120px] py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    isDrawing
                      ? "bg-leaf text-white ring-4 ring-leaf/50 shadow-lg"
                      : "bg-forest hover:bg-leaf text-white"
                  }`}
                  disabled={isSaving}
                  title="Använd ritverktyget i kartans övre vänstra hörn"
                >
                  {isDrawing ? "Ritar område..." : "Rita område"}
                </button>

                {/* Edit Button */}
                <button
                  onClick={handleEditToggle}
                  disabled={!polygonLayer || isSaving}
                  className={`flex-1 min-w-[120px] py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    polygonLayer && !isSaving
                      ? editMode
                        ? "bg-green-600 hover:bg-green-700 text-white"
                        : "bg-blue-600 hover:bg-blue-700 text-white"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  {editMode ? "Lås redigering" : "Redigera polygon"}
                </button>

                {/* Reset Button */}
                <button
                  onClick={handleReset}
                  disabled={!polygonLayer || isSaving}
                  className={`flex-1 min-w-[120px] py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    polygonLayer && !isSaving
                      ? "bg-red-600 hover:bg-red-600 text-white"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                Nollställ
                </button>

                {/* Save Button */}
                <button
                  onClick={handleSave}
                  disabled={!areaName.trim() || !areaDescription.trim() || !areaImage || !polygonLayer || isSaving}
                  className={`flex-1 min-w-[120px] py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    areaName.trim() && areaDescription.trim() && areaImage && polygonLayer && !isSaving
                      ? "bg-green-600 hover:bg-green-700 text-white"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  {isSaving ? "Sparar..." : "Spara"}
                </button>
              </div>
            </div>
          </div>
        </div>
      

      {/* Instructions Popup */}
      {showInstructions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
            <button
              onClick={() => setShowInstructions(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl font-bold"
            >
              ✕
            </button>
            <h3 className="font-heading text-xl font-bold text-charcoal mb-4">
              Instruktioner
            </h3>
            <div className="text-sm text-gray-700 space-y-2">
              <ul className="list-disc list-inside space-y-2">
                <li>Fyll i alla obligatoriska fält (*)</li>
                <li>Använd ritverktyget i kartans övre vänstra hörn (polygon-ikonen) för att rita området</li>
                <li>Klicka på kartan för att placera hörn i polygonen</li>
                <li>Dubbelklicka eller klicka på första punkten för att avsluta ritningen</li>
                <li>Använd redigeringsknappen för att flytta polygon-hörn efter att du ritat</li>
                <li>Klicka "Spara" när du är klar</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddNewAreaModal;
